import { useState, useEffect } from "react";
import axios from "axios";
import { useOfficerTitles } from "@/hooks/useOfficerTitles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit,
  Send,
  Lock,
  Unlock,
  BarChart3,
  Users,
  Calendar,
  ChevronDown,
  ChevronUp,
  Vote,
  CheckCircle,
  Clock,
  X,
  Eye,
  EyeOff,
  Archive,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Voting-restriction roles come from useOfficerTitles (DB-driven)

export default function Polls() {
  const [polls, setPolls] = useState([]);
  const [archivedPolls, setArchivedPolls] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);
  const [viewingResults, setViewingResults] = useState(null);
  const [viewingVoters, setViewingVoters] = useState(null);
  const [votingPoll, setVotingPoll] = useState(null);
  const [viewingArchive, setViewingArchive] = useState(null);
  const [canManage, setCanManage] = useState(false);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem("token");
  const userPermissions = JSON.parse(localStorage.getItem("permissions") || "{}");

  useEffect(() => {
    // Check manage permission - only based on explicit permission, not admin role
    const username = localStorage.getItem("username");
    const manage = userPermissions.manage_polls || username === "qball";
    setCanManage(manage);
    
    fetchPolls();
    if (manage) fetchArchivedPolls();
    
    // Check for vote parameter in URL
    const voteId = searchParams.get("vote");
    if (voteId) {
      // Will be handled after polls load
    }
  }, []);

  useEffect(() => {
    const voteId = searchParams.get("vote");
    if (voteId && polls.length > 0) {
      const poll = polls.find(p => p.id === voteId);
      if (poll && poll.status === "active") {
        setVotingPoll(poll);
      }
    }
  }, [polls, searchParams]);

  const fetchPolls = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/polls`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPolls(response.data.polls || []);
    } catch (error) {
      console.error("Failed to fetch polls:", error);
      toast.error("Failed to load polls");
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedPolls = async () => {
    try {
      const response = await axios.get(`${API}/polls/archived`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArchivedPolls(response.data.archived_polls || []);
    } catch (error) {
      console.error("Failed to fetch archived polls:", error);
    }
  };

  const handleViewArchivedDetails = async (pollId) => {
    try {
      const response = await axios.get(`${API}/polls/archived/${pollId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setViewingArchive(response.data);
    } catch (error) {
      toast.error("Failed to load archived poll details");
    }
  };

  const handleClearAllArchives = async () => {
    const lockedCount = archivedPolls.filter(p => p.locked).length;
    const unlockCount = archivedPolls.length - lockedCount;
    
    if (unlockCount === 0) {
      toast.error("All archived polls are locked. Unlock some to clear them.");
      return;
    }
    
    const message = lockedCount > 0 
      ? `Delete ${unlockCount} unlocked archived poll(s)? ${lockedCount} locked poll(s) will be preserved.`
      : "Are you sure you want to permanently delete ALL archived polls? This cannot be undone.";
    
    if (!confirm(message)) return;
    
    try {
      const response = await axios.delete(`${API}/polls/archived/clear-all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message || "Archives cleared");
      fetchArchivedPolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear archives");
    }
  };

  const handleLockArchive = async (pollId) => {
    try {
      await axios.post(`${API}/polls/archived/${pollId}/lock`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Poll locked - it won't be cleared");
      fetchArchivedPolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to lock poll");
    }
  };

  const handleUnlockArchive = async (pollId) => {
    try {
      await axios.post(`${API}/polls/archived/${pollId}/unlock`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Poll unlocked");
      fetchArchivedPolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlock poll");
    }
  };

  const handleDelete = async (pollId) => {
    if (!confirm("Archive this poll? The poll and all votes will be saved for future reference.")) return;
    
    try {
      await axios.delete(`${API}/polls/${pollId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Poll archived successfully");
      fetchPolls();
      fetchArchivedPolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete poll");
    }
  };

  const handlePublish = async (pollId) => {
    if (!confirm("Publish this poll? It will be posted to Discord and members can start voting.")) return;
    
    try {
      const response = await axios.post(`${API}/polls/${pollId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.discord_posted ? "Poll published and posted to Discord!" : "Poll published successfully");
      fetchPolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish poll");
    }
  };

  const handleClose = async (pollId) => {
    if (!confirm("Close this poll? Voting will end and results will be revealed.")) return;
    
    try {
      const response = await axios.post(`${API}/polls/${pollId}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Poll closed successfully");
      setViewingResults({ pollId, results: response.data.results, voterCount: response.data.voter_count });
      fetchPolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to close poll");
    }
  };

  const handleViewResults = async (poll) => {
    try {
      const response = await axios.get(`${API}/polls/${poll.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setViewingResults({ 
        poll: response.data, 
        results: response.data.results, 
        voterCount: response.data.voter_count 
      });
    } catch (error) {
      toast.error("Failed to load results");
    }
  };

  const handleViewVoters = async (pollId) => {
    try {
      const response = await axios.get(`${API}/polls/${pollId}/voters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setViewingVoters(response.data);
    } catch (error) {
      toast.error("Failed to load voters");
    }
  };

  const getStatusBadge = (poll) => {
    const now = new Date();
    const start = new Date(poll.start_date);
    const end = new Date(poll.end_date);
    
    if (poll.status === "draft") {
      return <span className="px-2 py-1 text-xs rounded-full bg-slate-600 text-slate-200">Draft</span>;
    }
    if (poll.status === "closed") {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-600/30 text-red-300">Closed</span>;
    }
    if (now < start) {
      return <span className="px-2 py-1 text-xs rounded-full bg-yellow-600/30 text-yellow-300">Scheduled</span>;
    }
    if (now > end) {
      return <span className="px-2 py-1 text-xs rounded-full bg-orange-600/30 text-orange-300">Expired</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-green-600/30 text-green-300">Active</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-3 md:gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="text-slate-400 hover:text-white shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white flex items-center gap-2 md:gap-3">
                <Vote className="w-6 h-6 md:w-8 md:h-8 text-purple-400" />
                Voting Polls
              </h1>
              <p className="text-slate-400 text-xs sm:text-sm mt-1 hidden sm:block">{canManage ? "Create and manage voting polls" : "Cast your vote in active polls"}</p>
            </div>
          </div>
          {canManage && (
            <div className="flex gap-2 ml-11 sm:ml-0">
              <Button
                variant={showArchived ? "outline" : "ghost"}
                onClick={() => setShowArchived(!showArchived)}
                className={`text-xs sm:text-sm ${showArchived ? "border-amber-500 text-amber-400" : "text-slate-400 hover:text-amber-400"}`}
                size="sm"
              >
                <Archive className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Archive</span> ({archivedPolls.length})
              </Button>
              <Button
                onClick={() => setShowCreateForm(true)}
                data-testid="create-poll-btn"
                className="bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm"
                size="sm"
              >
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Create Poll</span>
                <span className="sm:hidden">New</span>
              </Button>
            </div>
          )}
        </div>

        {/* Poll List */}
        {polls.length === 0 ? (
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
            <Vote className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Polls Yet</h3>
            <p className="text-slate-400">
              {canManage ? "Create your first poll to get started." : "No active polls available for voting."}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {polls.map((poll) => (
              <PollCard
                key={poll.id}
                poll={poll}
                canManage={canManage}
                onEdit={() => setEditingPoll(poll)}
                onDelete={() => handleDelete(poll.id)}
                onPublish={() => handlePublish(poll.id)}
                onClose={() => handleClose(poll.id)}
                onVote={() => setVotingPoll(poll)}
                onViewResults={() => handleViewResults(poll)}
                onViewVoters={() => handleViewVoters(poll.id)}
                getStatusBadge={getStatusBadge}
              />
            ))}
          </div>
        )}

        {/* Archived Polls Section */}
        {showArchived && archivedPolls.length > 0 && (
          <div className="mt-6 md:mt-8">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h2 className="text-lg md:text-xl font-semibold text-amber-400 flex items-center gap-2">
                <Archive className="w-5 h-5" />
                Archived Polls
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearAllArchives}
                className="border-red-700 text-red-400 hover:bg-red-900/20 text-xs"
                data-testid="clear-all-archives-btn"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Clear All
              </Button>
            </div>
            <div className="grid gap-2 sm:gap-3">
              {archivedPolls.map((archive) => (
                <div 
                  key={archive.poll_id}
                  className={`bg-slate-800/30 border rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${archive.locked ? 'border-green-700/50' : 'border-amber-900/30'}`}
                  data-testid={`archived-poll-${archive.poll_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {archive.locked && (
                        <Lock className="w-4 h-4 text-green-400 shrink-0" />
                      )}
                      <h3 className="text-white font-medium text-sm sm:text-base truncate">{archive.title}</h3>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mt-1">
                      <span>{archive.questions_count} Q{archive.questions_count !== 1 ? "s" : ""}</span>
                      <span>{archive.total_voters} voter{archive.total_voters !== 1 ? "s" : ""}</span>
                      <span className="hidden xs:inline">Archived {new Date(archive.archived_at).toLocaleDateString()}</span>
                      <span className="xs:hidden">{new Date(archive.archived_at).toLocaleDateString()}</span>
                      {archive.locked && (
                        <span className="text-green-400">Protected</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    {archive.locked ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnlockArchive(archive.poll_id)}
                        className="border-green-700 text-green-400 hover:bg-green-900/20 shrink-0"
                        title="Unlock to allow deletion"
                      >
                        <Unlock className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleLockArchive(archive.poll_id)}
                        className="border-slate-600 text-slate-400 hover:bg-slate-700/50 shrink-0"
                        title="Lock to prevent deletion"
                      >
                        <Lock className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewArchivedDetails(archive.poll_id)}
                      className="border-amber-700 text-amber-400 hover:bg-amber-900/20 flex-1 sm:flex-none shrink-0"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create/Edit Form Modal */}
        {(showCreateForm || editingPoll) && (
          <PollFormModal
            poll={editingPoll}
            onClose={() => {
              setShowCreateForm(false);
              setEditingPoll(null);
            }}
            onSave={() => {
              setShowCreateForm(false);
              setEditingPoll(null);
              fetchPolls();
            }}
            token={token}
          />
        )}

        {/* Voting Modal */}
        {votingPoll && (
          <VotingModal
            poll={votingPoll}
            onClose={() => setVotingPoll(null)}
            onVoted={() => {
              setVotingPoll(null);
              fetchPolls();
            }}
            token={token}
          />
        )}

        {/* Results Modal */}
        {viewingResults && (
          <ResultsModal
            data={viewingResults}
            onClose={() => setViewingResults(null)}
          />
        )}

        {/* Voters Modal */}
        {viewingVoters && (
          <VotersModal
            data={viewingVoters}
            onClose={() => setViewingVoters(null)}
          />
        )}

        {/* Archived Poll Details Modal */}
        {viewingArchive && (
          <ArchivedPollModal
            data={viewingArchive}
            onClose={() => setViewingArchive(null)}
          />
        )}
      </div>
    </div>
  );
}

// Poll Card Component
function PollCard({ poll, canManage, onEdit, onDelete, onPublish, onClose, onVote, onViewResults, onViewVoters, getStatusBadge }) {
  const [expanded, setExpanded] = useState(false);
  const hasVoted = poll.user_votes && Object.keys(poll.user_votes).length > 0;
  const isActive = poll.status === "active" && new Date() >= new Date(poll.start_date) && new Date() <= new Date(poll.end_date);
  const canVote = poll.can_vote !== false;
  
  return (
    <div className={`bg-slate-800/50 border rounded-xl overflow-hidden ${!canVote && isActive ? 'border-red-800/50' : 'border-slate-700'}`} data-testid={`poll-card-${poll.id}`}>
      <div 
        className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-slate-700/20 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3 mb-2 flex-wrap">
              <h3 className="text-base sm:text-lg font-semibold text-white break-words">{poll.title}</h3>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {getStatusBadge(poll)}
                {!canVote && isActive && (
                  <span className="px-2 py-0.5 sm:py-1 text-xs rounded-full bg-red-900/50 border border-red-700 text-red-300 flex items-center gap-1">
                    <Lock className="w-3 h-3" /> <span className="hidden sm:inline">Restricted</span>
                  </span>
                )}
                {hasVoted && isActive && canVote && (
                  <span className="px-2 py-0.5 sm:py-1 text-xs rounded-full bg-purple-600/30 text-purple-300 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> <span className="hidden sm:inline">Voted</span>
                  </span>
                )}
              </div>
            </div>
            {poll.description && (
              <p className="text-slate-400 text-xs sm:text-sm mb-2 line-clamp-2">{poll.description}</p>
            )}
            <div className="flex flex-wrap gap-x-3 gap-y-1 sm:gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span className="hidden sm:inline">{new Date(poll.start_date).toLocaleDateString()} - {new Date(poll.end_date).toLocaleDateString()}</span>
                <span className="sm:hidden">{new Date(poll.end_date).toLocaleDateString()}</span>
              </span>
              <span className="flex items-center gap-1">
                <BarChart3 className="w-3 h-3" />
                {poll.questions?.length || 0} Q{poll.questions?.length !== 1 ? "s" : ""}
              </span>
              {canManage && poll.voter_count !== undefined && (
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {poll.voter_count}
                </span>
              )}
              {poll.allowed_roles?.length > 0 && (
                <span className="flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  <span className="truncate max-w-[100px] sm:max-w-none">{poll.allowed_roles.slice(0, 2).join(", ")}{poll.allowed_roles.length > 2 ? "..." : ""}</span>
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center shrink-0">
            {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6 border-t border-slate-700/50 pt-3 sm:pt-4">
          {/* Questions Preview */}
          <div className="mb-4 space-y-2 sm:space-y-3">
            {poll.questions?.map((q, idx) => (
              <div key={idx} className="bg-slate-900/50 rounded-lg p-2.5 sm:p-3">
                <p className="text-xs sm:text-sm font-medium text-slate-300 mb-2">Q{idx + 1}: {q.question_text}</p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {q.answers?.map((a, aIdx) => (
                    <span key={aIdx} className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs bg-slate-700 rounded text-slate-300">
                      {String.fromCharCode(65 + aIdx)}. <span className="hidden sm:inline">{a.text}</span><span className="sm:hidden">{a.text.length > 15 ? a.text.substring(0, 15) + "..." : a.text}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {isActive && !canManage && canVote && (
              <Button onClick={onVote} className="bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm" size="sm" data-testid="vote-btn">
                <Vote className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{hasVoted ? "Change Vote" : "Vote Now"}</span>
                <span className="sm:hidden">Vote</span>
              </Button>
            )}
            {isActive && !canManage && !canVote && (
              <Button onClick={onVote} className="bg-red-900/50 border border-red-700 text-red-300 hover:bg-red-800/50 text-xs sm:text-sm" size="sm" data-testid="vote-btn">
                <Lock className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">View Restrictions</span>
                <span className="sm:hidden">Locked</span>
              </Button>
            )}
            {isActive && canManage && canVote && (
              <Button onClick={onVote} variant="outline" className="border-purple-500 text-purple-400 hover:bg-purple-500/10 text-xs sm:text-sm" size="sm" data-testid="vote-btn">
                <Vote className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{hasVoted ? "Change Vote" : "Vote"}</span>
                <span className="sm:hidden">Vote</span>
              </Button>
            )}
            {isActive && canManage && !canVote && (
              <Button onClick={onVote} variant="outline" className="border-red-600 text-red-400 hover:bg-red-600/10 text-xs sm:text-sm" size="sm" data-testid="vote-btn">
                <Lock className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Excluded</span>
                <span className="sm:hidden">Locked</span>
              </Button>
            )}
            {canManage && poll.status === "draft" && (
              <>
                <Button onClick={onEdit} variant="outline" data-testid="edit-poll-btn" className="border-slate-600 text-slate-300 text-xs sm:text-sm" size="sm">
                  <Edit className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Edit</span>
                </Button>
                <Button onClick={onPublish} className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm" size="sm">
                  <Send className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Publish</span>
                </Button>
                <Button onClick={onDelete} variant="outline" className="border-red-600 text-red-400 hover:bg-red-600/10 text-xs sm:text-sm" size="sm">
                  <Trash2 className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Delete</span>
                </Button>
              </>
            )}
            {canManage && poll.status === "active" && (
              <>
                <Button onClick={onClose} data-testid="close-poll-btn" className="bg-orange-600 hover:bg-orange-700 text-xs sm:text-sm" size="sm">
                  <Lock className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Close Poll</span>
                  <span className="sm:hidden">Close</span>
                </Button>
                <Button onClick={onViewVoters} variant="outline" className="border-slate-600 text-slate-300 text-xs sm:text-sm" size="sm">
                  <Users className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Who Voted</span>
                  <span className="sm:hidden">Voters</span>
                </Button>
              </>
            )}
            {(poll.status === "closed" || canManage) && (
              <Button onClick={onViewResults} variant="outline" data-testid="view-results-btn" className="border-slate-600 text-slate-300 text-xs sm:text-sm" size="sm">
                <BarChart3 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{poll.status === "closed" ? "View Results" : "Preview Results"}</span>
                <span className="sm:hidden">Results</span>
              </Button>
            )}
            {canManage && poll.status === "closed" && (
              <Button onClick={onDelete} variant="outline" className="border-red-600 text-red-400 hover:bg-red-600/10 text-xs sm:text-sm" size="sm">
                <Trash2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Delete</span>
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Poll Form Modal
function PollFormModal({ poll, onClose, onSave, token }) {
  const { all_titles: AVAILABLE_ROLES } = useOfficerTitles();
  const [title, setTitle] = useState(poll?.title || "");
  const [description, setDescription] = useState(poll?.description || "");
  const [questions, setQuestions] = useState(poll?.questions || [{ question_text: "", answers: [{ text: "" }, { text: "" }] }]);
  const [allowedRoles, setAllowedRoles] = useState(poll?.allowed_roles || []);
  const [excludedChapters, setExcludedChapters] = useState(poll?.excluded_chapters || []);
  const [excludedMembers, setExcludedMembers] = useState(poll?.excluded_members || []);
  const [startDate, setStartDate] = useState(poll?.start_date ? new Date(poll.start_date).toISOString().slice(0, 16) : "");
  const [endDate, setEndDate] = useState(poll?.end_date ? new Date(poll.end_date).toISOString().slice(0, 16) : "");
  const [discordChannel, setDiscordChannel] = useState(poll?.discord_channel || "member-votes");
  const [availableChannels, setAvailableChannels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [members, setMembers] = useState([]);
  const [showExclusions, setShowExclusions] = useState((poll?.excluded_chapters?.length > 0) || (poll?.excluded_members?.length > 0));

  // Prospect vote mode
  const [isProspectVote, setIsProspectVote] = useState(poll?.is_prospect_vote || false);
  const [selectedProspectIds, setSelectedProspectIds] = useState(poll?.prospect_ids || []);
  const [prospects, setProspects] = useState([]);
  const [allowVoteChanges, setAllowVoteChanges] = useState(poll ? poll.allow_vote_changes === true : false);
  const [anonymous, setAnonymous] = useState(poll?.anonymous !== false);
  const [bannerUrl, setBannerUrl] = useState(poll?.banner_url || "");
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/polls/upload-banner`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBannerUrl(res.data.url);
      toast.success("Banner uploaded — it will show on the Discord post");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Banner upload failed");
    } finally {
      setUploadingBanner(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    // Fetch chapters, members, and available channels.
    // Use allSettled so a single failed call doesn't wipe out the others
    // (e.g. earlier the prospects dropdown was always empty when one of the
    // parallel calls rejected).
    const fetchData = async () => {
      const [membersRes, channelsRes, prospectsRes] = await Promise.allSettled([
        axios.get(`${API}/members`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/polls/channels`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/prospects/poll-list`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const membersList = membersRes.status === "fulfilled" ? (membersRes.value.data || []) : [];
      setMembers(membersList);

      const prospectsList = prospectsRes.status === "fulfilled" ? (prospectsRes.value.data || []) : [];
      setProspects(prospectsList);

      // Extract unique chapters from members
      const uniqueChapters = [...new Set(membersList.map(m => m.chapter).filter(Boolean))].sort();
      setChapters(uniqueChapters);

      // Set available channels
      const channelsList = channelsRes.status === "fulfilled" ? (channelsRes.value.data || []) : [];
      setAvailableChannels(channelsList);

      // Surface failures to console for debugging without blocking UI
      [membersRes, channelsRes, prospectsRes].forEach((r, i) => {
        if (r.status === "rejected") {
          console.error(`Poll modal fetch ${["members","channels","prospects"][i]} failed:`, r.reason);
        }
      });
    };
    fetchData();
  }, [token]);

  const addQuestion = () => {
    setQuestions([...questions, { question_text: "", answers: [{ text: "" }, { text: "" }] }]);
  };

  const removeQuestion = (qIdx) => {
    if (questions.length <= 1) return;
    setQuestions(questions.filter((_, i) => i !== qIdx));
  };

  const updateQuestion = (qIdx, text) => {
    const newQuestions = [...questions];
    newQuestions[qIdx].question_text = text;
    setQuestions(newQuestions);
  };

  const addAnswer = (qIdx) => {
    const newQuestions = [...questions];
    newQuestions[qIdx].answers.push({ text: "" });
    setQuestions(newQuestions);
  };

  const removeAnswer = (qIdx, aIdx) => {
    if (questions[qIdx].answers.length <= 2) return;
    const newQuestions = [...questions];
    newQuestions[qIdx].answers = newQuestions[qIdx].answers.filter((_, i) => i !== aIdx);
    setQuestions(newQuestions);
  };

  const updateAnswer = (qIdx, aIdx, text) => {
    const newQuestions = [...questions];
    newQuestions[qIdx].answers[aIdx].text = text;
    setQuestions(newQuestions);
  };

  const toggleRole = (role) => {
    if (allowedRoles.includes(role)) {
      setAllowedRoles(allowedRoles.filter(r => r !== role));
    } else {
      setAllowedRoles([...allowedRoles, role]);
    }
  };

  const toggleChapter = (chapter) => {
    if (excludedChapters.includes(chapter)) {
      setExcludedChapters(excludedChapters.filter(c => c !== chapter));
    } else {
      setExcludedChapters([...excludedChapters, chapter]);
    }
  };

  const toggleMember = (memberId) => {
    if (excludedMembers.includes(memberId)) {
      setExcludedMembers(excludedMembers.filter(m => m !== memberId));
    } else {
      setExcludedMembers([...excludedMembers, memberId]);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Please enter a poll title");
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Please set start and end dates");
      return;
    }
    if (new Date(endDate) <= new Date(startDate)) {
      toast.error("End date must be after start date");
      return;
    }
    
    // Validate prospect-vote mode OR manual questions
    if (isProspectVote) {
      if (selectedProspectIds.length === 0) {
        toast.error("Pick at least one prospect to vote on");
        return;
      }
    } else {
      // Validate questions
      for (let i = 0; i < questions.length; i++) {
        if (!questions[i].question_text.trim()) {
          toast.error(`Question ${i + 1} is empty`);
          return;
        }
        for (let j = 0; j < questions[i].answers.length; j++) {
          if (!questions[i].answers[j].text.trim()) {
            toast.error(`Answer ${j + 1} in Question ${i + 1} is empty`);
            return;
          }
        }
      }
    }

    try {
      setSaving(true);
      const data = {
        title,
        description,
        questions: isProspectVote ? [] : questions,
        allowed_roles: allowedRoles,
        excluded_chapters: excludedChapters,
        excluded_members: excludedMembers,
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString(),
        discord_channel: discordChannel,
        is_prospect_vote: isProspectVote,
        prospect_ids: isProspectVote ? selectedProspectIds : [],
        allow_vote_changes: allowVoteChanges,
        anonymous,
        banner_url: bannerUrl || "",
      };

      if (poll) {
        await axios.put(`${API}/polls/${poll.id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Poll updated successfully");
      } else {
        await axios.post(`${API}/polls`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Poll created successfully");
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save poll");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-3 sm:p-4 flex items-center justify-between z-10">
          <h2 className="text-lg sm:text-xl font-bold text-white">{poll ? "Edit Poll" : "Create New Poll"}</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0">
            <X className="w-5 h-5 text-slate-400" />
          </Button>
        </div>

        <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
          {/* Basic Info */}
          <div className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">Poll Title *</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter poll title"
                className="bg-slate-900 border-slate-600 text-white text-sm"
                data-testid="poll-title-input"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">Description (optional)</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter poll description"
                className="bg-slate-900 border-slate-600 text-white text-sm"
                rows={2}
              />
            </div>

            {/* Prospect Vote toggle */}
            <div>
              <button
                type="button"
                onClick={() => setIsProspectVote(!isProspectVote)}
                disabled={!!poll}
                data-testid="prospect-vote-toggle"
                className={`w-full p-3 rounded-lg border text-left transition-all ${
                  isProspectVote
                    ? 'bg-amber-900/30 border-amber-600 ring-1 ring-amber-500/50'
                    : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                } ${poll ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    isProspectVote ? 'bg-amber-600 border-amber-600' : 'border-slate-600'
                  }`}>
                    {isProspectVote && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Prospect Vote (promotion to Brother)</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Auto-builds a Yes/No question per prospect. Only Discord members with the <span className="font-semibold text-amber-300">Voting</span> role can vote, and a reason is required for No votes.
                    </p>
                  </div>
                </div>
                {poll && <p className="text-[10px] text-slate-500 mt-2">Cannot change vote type after creation.</p>}
              </button>
            </div>

            {/* Lock votes toggle */}
            <div>
              <button
                type="button"
                onClick={() => setAllowVoteChanges(!allowVoteChanges)}
                data-testid="allow-vote-changes-toggle"
                className={`w-full p-3 rounded-lg border text-left transition-all cursor-pointer ${
                  !allowVoteChanges
                    ? 'bg-red-900/20 border-red-700 ring-1 ring-red-500/40'
                    : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    !allowVoteChanges ? 'bg-red-600 border-red-600' : 'border-slate-600'
                  }`}>
                    {!allowVoteChanges && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Lock votes (no changes allowed)</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      When enabled, each member's first vote is final — they cannot change their answer afterward.
                    </p>
                  </div>
                </div>
              </button>
            </div>

            {/* Banner image for the Discord post */}
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-1.5">Discord Banner Image <span className="text-slate-500 font-normal">(optional)</span></label>
              {bannerUrl ? (
                <div className="relative rounded-lg overflow-hidden border border-slate-700" data-testid="poll-banner-preview">
                  <img src={`${BACKEND_URL}${bannerUrl}`} alt="Poll banner" className="w-full max-h-40 object-cover" />
                  <button
                    type="button"
                    onClick={() => setBannerUrl("")}
                    className="absolute top-2 right-2 bg-black/70 hover:bg-red-900/80 text-white rounded-full p-1.5"
                    data-testid="poll-banner-remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className={`flex items-center justify-center gap-2 w-full p-4 rounded-lg border-2 border-dashed border-slate-700 hover:border-purple-600 cursor-pointer text-slate-400 hover:text-purple-300 transition-colors text-sm ${uploadingBanner ? "opacity-50 pointer-events-none" : ""}`} data-testid="poll-banner-upload">
                  {uploadingBanner ? "Uploading…" : "Click to upload a banner photo (JPG/PNG/GIF/WEBP, max 8MB) — shown at the top of the Discord post"}
                  <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleBannerUpload} />
                </label>
              )}
            </div>

            {/* Public (non-anonymous) votes toggle */}
            <div>
              <button
                type="button"
                onClick={() => setAnonymous(!anonymous)}
                data-testid="anonymous-toggle"
                className={`w-full p-3 rounded-lg border text-left transition-all cursor-pointer ${
                  !anonymous
                    ? 'bg-amber-900/20 border-amber-700 ring-1 ring-amber-500/40'
                    : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    !anonymous ? 'bg-amber-600 border-amber-600' : 'border-slate-600'
                  }`}>
                    {!anonymous && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Public votes (not anonymous)</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      When enabled, each voter's name is shown next to their answer in the results. Voters are warned before voting.
                    </p>
                  </div>
                </div>
              </button>
            </div>

            {/* Prospect picker — only when prospect vote */}
            {isProspectVote && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">
                  Prospects to Vote On * <span className="text-slate-500 font-normal">({selectedProspectIds.length} selected)</span>
                </label>
                {prospects.length === 0 ? (
                  <div className="p-4 bg-slate-900 border border-slate-700 rounded-lg text-sm text-slate-400 text-center">
                    No prospects in the system. Add a prospect on the Prospects page first.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 bg-slate-900 border border-slate-700 rounded-lg" data-testid="prospect-vote-picker">
                    {prospects.map((p) => {
                      const checked = selectedProspectIds.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            if (poll) return; // locked when editing
                            setSelectedProspectIds((prev) =>
                              prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id]
                            );
                          }}
                          disabled={!!poll}
                          data-testid={`prospect-pick-${p.id}`}
                          className={`flex items-center gap-2 p-2 rounded border text-sm text-left transition-all ${
                            checked
                              ? 'bg-amber-600/30 border-amber-500'
                              : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                          } ${poll ? 'cursor-not-allowed' : ''}`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            checked ? 'bg-amber-600 border-amber-600' : 'border-slate-600'
                          }`}>
                            {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
                          </div>
                          <span className="text-white truncate">{p.handle || p.name}</span>
                          {p.chapter && <span className="ml-auto text-[10px] text-slate-400">{p.chapter}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">Start Date *</label>
              <Input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-slate-900 border-slate-600 text-white text-sm"
                data-testid="poll-start-date"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">End Date *</label>
              <Input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-slate-900 border-slate-600 text-white text-sm"
                data-testid="poll-end-date"
              />
            </div>
          </div>

          {/* Discord Channel Selection */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">
              Post to Discord Channel
            </label>
            <select
              value={discordChannel}
              onChange={(e) => setDiscordChannel(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 text-white text-sm rounded-md px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              data-testid="poll-discord-channel"
            >
              {availableChannels.length > 0 ? (
                availableChannels.map((ch) => (
                  <option key={ch.id} value={ch.id}>{ch.name}</option>
                ))
              ) : (
                <>
                  <option value="member-votes">Member Votes (Everyone)</option>
                  <option value="national-board-vote">National Board Vote</option>
                </>
              )}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Choose where this poll will be posted on Discord
            </p>
          </div>

          {/* Role Restrictions */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-1.5 sm:mb-2">
              Who Can Vote? <span className="text-slate-500 text-xs">(leave empty for all)</span>
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5 sm:gap-2 p-2 sm:p-3 bg-slate-900/50 rounded-lg border border-slate-700 max-h-36 overflow-y-auto">
              {AVAILABLE_ROLES.map((role) => (
                <label key={role} className="flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg hover:bg-slate-700/50 cursor-pointer">
                  <Checkbox
                    checked={allowedRoles.includes(role)}
                    onCheckedChange={() => toggleRole(role)}
                    className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                  />
                  <span className="text-xs sm:text-sm text-slate-300 truncate">{role}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Exclusions Section */}
          <div>
            <button
              type="button"
              onClick={() => setShowExclusions(!showExclusions)}
              className="flex items-center gap-2 text-xs sm:text-sm font-medium text-red-400 hover:text-red-300"
            >
              {showExclusions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Exclude Chapters or Members {(excludedChapters.length > 0 || excludedMembers.length > 0) && `(${excludedChapters.length + excludedMembers.length})`}
            </button>
            
            {showExclusions && (
              <div className="mt-2 sm:mt-3 space-y-3 sm:space-y-4 p-3 sm:p-4 bg-red-950/20 border border-red-900/30 rounded-lg">
                {/* Excluded Chapters */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-red-300 mb-1.5 sm:mb-2">
                    Exclude Chapters ({excludedChapters.length})
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5 sm:gap-2 max-h-28 sm:max-h-32 overflow-y-auto">
                    {chapters.map((chapter) => (
                      <label key={chapter} className="flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg hover:bg-red-900/30 cursor-pointer border border-red-900/30">
                        <Checkbox
                          checked={excludedChapters.includes(chapter)}
                          onCheckedChange={() => toggleChapter(chapter)}
                          className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                        />
                        <span className="text-xs sm:text-sm text-red-200 truncate">{chapter}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Excluded Members */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-red-300 mb-1.5 sm:mb-2">
                    Exclude Members ({excludedMembers.length})
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2 max-h-36 sm:max-h-48 overflow-y-auto">
                    {members.map((member) => (
                      <label key={member.id} className="flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg hover:bg-red-900/30 cursor-pointer border border-red-900/30">
                        <Checkbox
                          checked={excludedMembers.includes(member.id)}
                          onCheckedChange={() => toggleMember(member.id)}
                          className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                        />
                        <span className="text-xs sm:text-sm text-red-200 truncate">{member.handle || member.legal_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Questions */}
          {!isProspectVote && (
          <div>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <label className="block text-xs sm:text-sm font-medium text-slate-300">Questions *</label>
              <Button onClick={addQuestion} variant="outline" size="sm" className="border-purple-500 text-purple-400 text-xs">
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Add
              </Button>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {questions.map((q, qIdx) => (
                <div key={qIdx} className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start gap-2 mb-2 sm:mb-3">
                    <span className="text-purple-400 font-medium text-sm shrink-0">Q{qIdx + 1}.</span>
                    <Input
                      value={q.question_text}
                      onChange={(e) => updateQuestion(qIdx, e.target.value)}
                      placeholder="Enter question"
                      className="flex-1 bg-slate-800 border-slate-600 text-white text-sm"
                      data-testid={`question-${qIdx}-input`}
                    />
                    {questions.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeQuestion(qIdx)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0 h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="space-y-2 ml-0 sm:ml-6">
                    {q.answers.map((a, aIdx) => (
                      <div key={aIdx} className="flex items-center gap-2">
                        <span className="text-slate-500 text-xs sm:text-sm w-5 sm:w-6 shrink-0">{String.fromCharCode(65 + aIdx)}.</span>
                        <Input
                          value={a.text}
                          onChange={(e) => updateAnswer(qIdx, aIdx, e.target.value)}
                          placeholder={`Answer ${aIdx + 1}`}
                          className="flex-1 bg-slate-800 border-slate-600 text-white text-xs sm:text-sm"
                          data-testid={`answer-${qIdx}-${aIdx}-input`}
                        />
                        {q.answers.length > 2 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeAnswer(qIdx, aIdx)}
                            className="text-slate-400 hover:text-red-400 shrink-0 h-7 w-7 sm:h-8 sm:w-8"
                          >
                            <X className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      onClick={() => addAnswer(qIdx)}
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-purple-400 ml-5 sm:ml-6 text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Answer
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-800 border-t border-slate-700 p-3 sm:p-4 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 w-full sm:w-auto">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto" data-testid="save-poll-btn">
            {saving ? "Saving..." : (poll ? "Update Poll" : "Create Poll")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Voting Modal
function VotingModal({ poll, onClose, onVoted, token }) {
  const [votes, setVotes] = useState(poll.user_votes || {});
  const [submitting, setSubmitting] = useState(false);
  const [noReasons, setNoReasons] = useState({}); // {questionIndex: reasonText}
  const [pendingNoQuestion, setPendingNoQuestion] = useState(null); // {qIdx, aIdx}
  const [pendingNoReason, setPendingNoReason] = useState("");

  // Check if user can vote
  if (poll.can_vote === false) {
    const hasRoleRestriction = poll.allowed_roles && poll.allowed_roles.length > 0;
    const hasChapterExclusion = poll.excluded_chapters && poll.excluded_chapters.length > 0;
    const hasMemberExclusion = poll.excluded_members && poll.excluded_members.length > 0;
    
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-gradient-to-br from-red-950 via-black to-red-950 border-2 border-red-600 rounded-xl w-full max-w-md shadow-2xl shadow-red-900/50">
          <div className="p-5 sm:p-8 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-red-900/50 border-2 border-red-500 flex items-center justify-center">
              <Lock className="w-8 h-8 sm:w-10 sm:h-10 text-red-400" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-red-400 mb-2 sm:mb-3">Voting Restricted</h2>
            <p className="text-slate-300 text-sm sm:text-base mb-2">{poll.title}</p>
            <div className="bg-black/50 border border-red-800 rounded-lg p-3 sm:p-4 mt-3 sm:mt-4 mb-4 sm:mb-6 space-y-3">
              {hasRoleRestriction && (
                <div>
                  <p className="text-red-300 text-xs sm:text-sm mb-2">Restricted to roles:</p>
                  <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                    {poll.allowed_roles.map((role, idx) => (
                      <span key={idx} className="px-2 sm:px-3 py-0.5 sm:py-1 bg-red-900/50 border border-red-700 rounded-full text-red-200 text-xs sm:text-sm font-medium">
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {hasChapterExclusion && (
                <div>
                  <p className="text-red-300 text-xs sm:text-sm mb-2">Excluded chapters:</p>
                  <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                    {poll.excluded_chapters.map((chapter, idx) => (
                      <span key={idx} className="px-2 sm:px-3 py-0.5 sm:py-1 bg-red-900/50 border border-red-700 rounded-full text-red-200 text-xs sm:text-sm font-medium">
                        {chapter}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {hasMemberExclusion && (
                <p className="text-red-300 text-xs sm:text-sm">You have been specifically excluded from this poll.</p>
              )}
            </div>
            <p className="text-slate-400 text-xs sm:text-sm mb-4 sm:mb-6">You do not have permission to vote on this poll.</p>
            <Button 
              onClick={onClose} 
              className="bg-red-700 hover:bg-red-600 text-white px-6 sm:px-8 w-full sm:w-auto"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleVote = async (questionIndex, answerIndex, noReason = null) => {
    try {
      setSubmitting(true);
      await axios.post(`${API}/polls/${poll.id}/vote`, {
        question_index: questionIndex,
        answer_index: answerIndex,
        no_reason: noReason,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setVotes({ ...votes, [questionIndex]: answerIndex });
      if (noReason) {
        setNoReasons({ ...noReasons, [questionIndex]: noReason });
      }
      toast.success("Vote submitted!");
      
      // Check if all questions answered
      if (Object.keys({ ...votes, [questionIndex]: answerIndex }).length === poll.questions.length) {
        setTimeout(() => {
          onVoted();
        }, 500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit vote");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswerClick = (qIdx, aIdx) => {
    if (poll.allow_vote_changes !== true && votes[qIdx] !== undefined) {
      toast.error("This poll does not allow vote changes — your vote is final");
      return;
    }
    if (poll.is_prospect_vote) {
      const answerText = (poll.questions[qIdx]?.answers?.[aIdx]?.text || "").trim().toLowerCase();
      if (answerText === "no") {
        // Open reason prompt
        setPendingNoQuestion({ qIdx, aIdx });
        setPendingNoReason(noReasons[qIdx] || "");
        return;
      }
    }
    handleVote(qIdx, aIdx);
  };

  const submitPendingNoReason = () => {
    const reason = pendingNoReason.trim();
    if (!reason) {
      toast.error("Please provide a reason for your No vote");
      return;
    }
    const { qIdx, aIdx } = pendingNoQuestion;
    handleVote(qIdx, aIdx, reason);
    setPendingNoQuestion(null);
    setPendingNoReason("");
  };

  const allVoted = Object.keys(votes).length === poll.questions.length;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-3 sm:p-4 flex items-center justify-between z-10">
          <div className="min-w-0 flex-1 pr-2">
            <h2 className="text-lg sm:text-xl font-bold text-white truncate">{poll.title}</h2>
            {poll.description && <p className="text-slate-400 text-xs sm:text-sm mt-1 line-clamp-1">{poll.description}</p>}
            {poll.allow_vote_changes !== true && (
              <p className="text-amber-400 text-[11px] sm:text-xs mt-1 font-medium" data-testid="votes-final-notice">
                ⚠️ Votes are final on this poll — you cannot change your answer after voting.
              </p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0">
            <X className="w-5 h-5 text-slate-400" />
          </Button>
        </div>

        <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
          {poll.banner_url && (
            <img src={`${BACKEND_URL}${poll.banner_url}`} alt="Poll banner" className="w-full max-h-48 object-cover rounded-lg border border-slate-700" data-testid="voting-banner" />
          )}
          {poll.anonymous === false ? (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 sm:p-3 text-center" data-testid="public-vote-warning">
              <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400 mx-auto mb-1" />
              <p className="text-amber-300 text-xs sm:text-sm">This is a PUBLIC poll — your name will be shown next to your answer in the results.</p>
            </div>
          ) : (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-2.5 sm:p-3 text-center">
              <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 mx-auto mb-1" />
              <p className="text-purple-300 text-xs sm:text-sm">Your vote is anonymous. Only admins can see who voted, not what you voted for.</p>
            </div>
          )}

          {poll.questions.map((q, qIdx) => (
            <div key={qIdx} className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 sm:p-4">
              <h3 className="text-white font-medium mb-3 sm:mb-4 text-sm sm:text-base">
                <span className="text-purple-400">Q{qIdx + 1}.</span> {q.question_text}
              </h3>
              <div className="space-y-2">
                {q.answers.map((a, aIdx) => (
                  <button
                    key={aIdx}
                    onClick={() => handleAnswerClick(qIdx, aIdx)}
                    disabled={submitting || (poll.allow_vote_changes !== true && votes[qIdx] !== undefined && votes[qIdx] !== aIdx)}
                    className={`w-full text-left p-2.5 sm:p-3 rounded-lg border transition-all text-sm ${
                      votes[qIdx] === aIdx
                        ? "bg-purple-600/30 border-purple-500 text-white"
                        : poll.allow_vote_changes !== true && votes[qIdx] !== undefined
                        ? "bg-slate-800 border-slate-700 text-slate-500 opacity-60 cursor-not-allowed"
                        : "bg-slate-800 border-slate-600 text-slate-300 hover:border-purple-500/50 hover:bg-slate-700"
                    }`}
                    data-testid={`vote-option-${qIdx}-${aIdx}`}
                  >
                    <span className="font-medium mr-2">{String.fromCharCode(65 + aIdx)}.</span>
                    {a.text}
                    {votes[qIdx] === aIdx && (
                      <CheckCircle className="w-4 h-4 text-purple-400 float-right mt-0.5" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 bg-slate-800 border-t border-slate-700 p-3 sm:p-4 flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
          <span className="text-slate-400 text-xs sm:text-sm">
            {Object.keys(votes).length} of {poll.questions.length} answered
          </span>
          <Button onClick={onClose} className={`w-full sm:w-auto ${allVoted ? "bg-green-600 hover:bg-green-700" : "bg-slate-600"}`}>
            {allVoted ? "Done" : "Close"}
          </Button>
        </div>
      </div>

      {/* No-Reason Prompt Modal (prospect votes only) */}
      {pendingNoQuestion && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-3 sm:p-4">
          <div className="bg-slate-900 border border-amber-600 rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-4 sm:p-5 border-b border-amber-700/40">
              <h3 className="text-base sm:text-lg font-bold text-white">Reason Required</h3>
              <p className="text-xs sm:text-sm text-amber-300 mt-1">
                Why are you voting <span className="font-bold">No</span>?
              </p>
              <p className="text-[11px] text-slate-400 mt-1">
                {poll.questions[pendingNoQuestion.qIdx]?.question_text}
              </p>
            </div>
            <div className="p-4 sm:p-5">
              <Textarea
                value={pendingNoReason}
                onChange={(e) => setPendingNoReason(e.target.value)}
                placeholder="Brief reason for your No vote..."
                rows={4}
                autoFocus
                data-testid="no-reason-textarea"
                className="bg-slate-800 border-slate-600 text-white text-sm"
              />
              <p className="text-[11px] text-slate-500 mt-2">
                Visible to officers (Prez/VP/SEC/T or anyone with the manage_polls permission). Other voters cannot see your reason.
              </p>
            </div>
            <div className="p-4 sm:p-5 border-t border-slate-700 flex flex-col-reverse sm:flex-row justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => { setPendingNoQuestion(null); setPendingNoReason(""); }}
                className="border-slate-600 text-slate-300 w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={submitPendingNoReason}
                disabled={submitting || !pendingNoReason.trim()}
                data-testid="submit-no-reason-btn"
                className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto"
              >
                {submitting ? "Saving..." : "Submit No Vote"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Results Modal
function ResultsModal({ data, onClose }) {
  const poll = data.poll || {};
  const results = data.results || {};
  const voterCount = data.voterCount || 0;
  const votersByAnswer = poll.voters_by_answer || null;
  const isPublicVote = poll.anonymous === false;

  const [noReasons, setNoReasons] = useState([]);
  const [canSeeAll, setCanSeeAll] = useState(false);

  useEffect(() => {
    if (!poll?.id || !poll?.is_prospect_vote) return;
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/polls/${poll.id}/prospect-no-reasons`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setNoReasons(res.data?.no_reasons || []);
        setCanSeeAll(!!res.data?.can_see_all);
      })
      .catch(() => {});
  }, [poll?.id, poll?.is_prospect_vote]);

  // Group reasons by question_index for quick lookup
  const reasonsByQ = noReasons.reduce((acc, r) => {
    const k = r.question_index ?? 0;
    (acc[k] = acc[k] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-3 sm:p-4 flex items-center justify-between z-10">
          <div className="min-w-0 flex-1 pr-2">
            <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-400 shrink-0" />
              <span className="truncate">Poll Results</span>
            </h2>
            <p className="text-slate-400 text-xs sm:text-sm mt-1 truncate">{poll.title}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0">
            <X className="w-5 h-5 text-slate-400" />
          </Button>
        </div>

        <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
          <div className="flex items-center justify-center gap-2 text-slate-300">
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
            <span className="text-base sm:text-lg font-medium">{voterCount} Total Voter{voterCount !== 1 ? "s" : ""}</span>
          </div>
          {isPublicVote && (
            <p className="text-center text-amber-400/90 text-xs" data-testid="public-vote-note">
              Public poll — voter names are shown with their answers.
            </p>
          )}

          {poll.questions?.map((q, qIdx) => {
            const qResult = results[qIdx] || { total_votes: 0, answers: {} };
            return (
              <div key={qIdx} className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 sm:p-4">
                <h3 className="text-white font-medium mb-3 sm:mb-4 text-sm sm:text-base">
                  <span className="text-purple-400">Q{qIdx + 1}.</span> {q.question_text}
                  <span className="text-slate-500 text-xs sm:text-sm ml-2">({qResult.total_votes} votes)</span>
                </h3>
                <div className="space-y-2.5 sm:space-y-3">
                  {q.answers?.map((a, aIdx) => {
                    const aResult = qResult.answers?.[aIdx] || { count: 0, percentage: 0 };
                    return (
                      <div key={aIdx}>
                        <div className="flex justify-between text-xs sm:text-sm mb-1 gap-2">
                          <span className="text-slate-300 truncate flex-1">
                            <span className="font-medium">{String.fromCharCode(65 + aIdx)}.</span> {a.text}
                          </span>
                          <span className="text-purple-400 shrink-0">{aResult.count} ({aResult.percentage}%)</span>
                        </div>
                        <div className="h-2.5 sm:h-3 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-purple-600 to-purple-400 transition-all duration-500"
                            style={{ width: `${aResult.percentage}%` }}
                          />
                        </div>
                        {isPublicVote && votersByAnswer?.[qIdx]?.[aIdx]?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`voters-q${qIdx}-a${aIdx}`}>
                            {votersByAnswer[qIdx][aIdx].map((name, i) => (
                              <span key={i} className="px-1.5 py-0.5 text-[10px] rounded bg-amber-900/30 border border-amber-700/50 text-amber-200">
                                {name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Prospect-vote No reasons (officers see all, voters see own only) */}
                {poll.is_prospect_vote && (reasonsByQ[qIdx]?.length > 0) && (
                  <div className="mt-3 pt-3 border-t border-amber-700/30" data-testid={`no-reasons-q-${qIdx}`}>
                    <p className="text-xs font-semibold text-amber-300 mb-2 flex items-center gap-1">
                      <span>🚫</span>
                      No-Vote Reasons {canSeeAll ? `(${reasonsByQ[qIdx].length})` : "(yours)"}
                    </p>
                    <div className="space-y-1.5">
                      {reasonsByQ[qIdx].map((r, i) => (
                        <div key={i} className="bg-amber-900/15 border border-amber-700/40 rounded p-2 text-xs">
                          <div className="text-slate-200 italic">"{r.no_reason}"</div>
                          {canSeeAll && (
                            <div className="text-[10px] text-amber-300/80 mt-1">— {r.user_id}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="sticky bottom-0 bg-slate-800 border-t border-slate-700 p-3 sm:p-4 flex justify-end">
          <Button onClick={onClose} className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

// Voters Modal (who voted, not what)
function VotersModal({ data, onClose }) {
  const voters = data.voters || [];
  const count = data.count || 0;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-3 sm:p-4 flex items-center justify-between z-10">
          <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            Who Voted ({count})
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0">
            <X className="w-5 h-5 text-slate-400" />
          </Button>
        </div>

        <div className="p-3 sm:p-4">
          <p className="text-slate-400 text-xs sm:text-sm mb-3 sm:mb-4 flex items-center gap-2">
            <EyeOff className="w-4 h-4 shrink-0" />
            Only showing who voted, not their answers
          </p>
          
          {voters.length === 0 ? (
            <p className="text-slate-500 text-center py-6 sm:py-8">No votes yet</p>
          ) : (
            <div className="space-y-2">
              {voters.map((voter, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 p-2.5 sm:p-3 bg-slate-900/50 rounded-lg">
                  <span className="text-white text-sm sm:text-base font-medium">{voter.handle}</span>
                  <span className="text-slate-500 text-xs sm:text-sm">{voter.title} - {voter.chapter}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-slate-800 border-t border-slate-700 p-3 sm:p-4 flex justify-end">
          <Button onClick={onClose} className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

// Archived Poll Modal
function ArchivedPollModal({ data, onClose }) {
  const poll = data.poll_data || {};
  const results = data.results || {};
  const totalVoters = data.total_voters || 0;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-slate-800 border border-amber-900/50 rounded-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-amber-900/50 p-3 sm:p-4 flex items-center justify-between z-10">
          <div className="min-w-0 flex-1 pr-2">
            <h2 className="text-lg sm:text-xl font-bold text-amber-400 flex items-center gap-2">
              <Archive className="w-5 h-5 shrink-0" />
              <span className="truncate">Archived Poll</span>
            </h2>
            <p className="text-white text-sm mt-1 truncate">{poll.title}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0">
            <X className="w-5 h-5 text-slate-400" />
          </Button>
        </div>

        <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
          {poll.description && (
            <p className="text-slate-400 text-sm">{poll.description}</p>
          )}

          <div className="flex flex-wrap gap-x-3 gap-y-1.5 sm:gap-4 text-xs sm:text-sm text-slate-400">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">{new Date(poll.start_date).toLocaleDateString()} - {new Date(poll.end_date).toLocaleDateString()}</span>
              <span className="sm:hidden">{new Date(poll.end_date).toLocaleDateString()}</span>
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
              {totalVoters} voter{totalVoters !== 1 ? "s" : ""}
            </span>
            {poll.allowed_roles?.length > 0 && (
              <span className="flex items-center gap-1">
                <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="truncate max-w-[120px] sm:max-w-none">{poll.allowed_roles.slice(0, 3).join(", ")}{poll.allowed_roles.length > 3 ? "..." : ""}</span>
              </span>
            )}
          </div>

          <div className="text-xs text-slate-500">
            Archived on {new Date(data.archived_at).toLocaleString()} by {data.archived_by}
          </div>

          {/* Results */}
          {poll.questions?.map((q, qIdx) => {
            const qResult = results[qIdx] || { total_votes: 0, answers: {} };
            return (
              <div key={qIdx} className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 sm:p-4">
                <h3 className="text-white font-medium mb-3 sm:mb-4 text-sm sm:text-base">
                  <span className="text-amber-400">Q{qIdx + 1}.</span> {q.question_text}
                  <span className="text-slate-500 text-xs sm:text-sm ml-2">({qResult.total_votes} votes)</span>
                </h3>
                <div className="space-y-2.5 sm:space-y-3">
                  {q.answers?.map((a, aIdx) => {
                    const aResult = qResult.answers?.[aIdx] || { count: 0, percentage: 0 };
                    return (
                      <div key={aIdx}>
                        <div className="flex justify-between text-xs sm:text-sm mb-1 gap-2">
                          <span className="text-slate-300 truncate flex-1">
                            <span className="font-medium">{String.fromCharCode(65 + aIdx)}.</span> {a.text}
                          </span>
                          <span className="text-amber-400 shrink-0">{aResult.count} ({aResult.percentage}%)</span>
                        </div>
                        <div className="h-2.5 sm:h-3 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-amber-600 to-amber-400 transition-all duration-500"
                            style={{ width: `${aResult.percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="sticky bottom-0 bg-slate-800 border-t border-amber-900/50 p-3 sm:p-4 flex justify-end">
          <Button onClick={onClose} className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
